import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AppNavigationItem, AppSidebarComponent } from '@clinic-platform/ui-components';
import { Subscription, catchError, filter, forkJoin, map, of } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { UserRole } from '../auth/user-role.type';
import { AppLanguage } from '../i18n/i18n.models';
import { I18nService } from '../i18n/i18n.service';
import { TranslatePipe } from '../i18n/translate.pipe';
import { AppointmentsService } from '../../features/appointments/data-access/appointments.service';
import { DowntimeService } from '../../features/downtime/data-access/downtime.service';
import { SpecialistDowntimeService } from '../../features/specialist-downtime/data-access/specialist-downtime.service';
import { SystemAdminContextService } from '../context/system-admin-context.service';
import { TenantBrandingService } from '../branding/tenant-branding.service';

@Component({
  selector: 'app-shell-sidebar',
  standalone: true,
  imports: [
    AppSidebarComponent,
    TranslatePipe,
    RouterLink,
    RouterLinkActive
  ],
  template: `
    <lib-app-sidebar>
      @for (item of navigationItems; track item.route) {
        <a
          [routerLink]="toRoute(item.route)"
          routerLinkActive="app-sidebar__link--active"
          class="app-sidebar__link"
        >
          <span class="app-sidebar__link-label">{{ item.labelKey | t }}</span>
          @if (isAppointmentsRoute(item.route) && appointmentsHasPending) {
            <span
              class="app-sidebar__pending-dot"
              aria-label="Pending appointments"
              title="Pending appointments"
            ></span>
          }
          @if (isDowntimeRoute(item.route) && downtimeHasPending) {
            <span
              class="app-sidebar__pending-dot"
              aria-label="Pending downtime approvals"
              title="Pending downtime approvals"
            ></span>
          }
        </a>
      }

      <div libAppSidebarFooter class="app-sidebar__footer-panel">
        <div class="app-sidebar__pref-field">
          <label class="app-sidebar__pref-label" for="sidebar-lang-select">{{ 'app.language' | t }}</label>
          <select
            id="sidebar-lang-select"
            class="app-sidebar__pref-select"
            [value]="currentLanguage"
            (change)="onLanguageChange($any($event.target).value)"
          >
            @for (option of languageOptions; track option.code) {
              <option [value]="option.code">{{ option.label }}</option>
            }
          </select>
        </div>

        @if (userEmail) {
          <div class="app-sidebar__account">
            <div class="app-sidebar__account-email">{{ userEmail }}</div>
            @if (userRole) {
              <div class="app-sidebar__account-role">{{ userRole }}</div>
            }
          </div>
        }

        <button
          type="button"
          class="app-sidebar__logout"
          (click)="onLogout()"
        >
          {{ 'app.logout' | t }}
        </button>
      </div>
    </lib-app-sidebar>
  `,
  styles: [`
    .app-sidebar__footer-panel {
      display: grid;
      gap: 12px;
    }

    .app-sidebar__pref-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }

    .app-sidebar__pref-label {
      font-size: 12px;
      letter-spacing: 0.04em;
      color: color-mix(in srgb, var(--tenant-sidebar-text, #ffffff) 72%, transparent);
      text-transform: uppercase;
    }

    .app-sidebar__pref-select {
      height: 38px;
      border: 1px solid color-mix(in srgb, var(--tenant-sidebar-text, #ffffff) 18%, transparent);
      border-radius: 10px;
      padding: 0 10px;
      background: color-mix(in srgb, var(--tenant-sidebar-bg, #0f172a) 84%, white);
      color: var(--tenant-sidebar-text, #ffffff);
      width: 100%;
    }

    .app-sidebar__account {
      display: grid;
      gap: 2px;
      min-width: 0;
    }

    .app-sidebar__account-email {
      color: var(--tenant-sidebar-text, #ffffff);
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .app-sidebar__account-role {
      color: color-mix(in srgb, var(--tenant-sidebar-text, #ffffff) 70%, transparent);
      font-size: 12px;
    }

    .app-sidebar__logout {
      border: none;
      background: color-mix(in srgb, var(--tenant-secondary, #111827) 78%, white);
      color: #ffffff;
      min-height: 40px;
      padding: 10px 14px;
      border-radius: 10px;
      cursor: pointer;
      white-space: nowrap;
      width: 100%;
    }

    .app-sidebar__logout:hover {
      background: color-mix(in srgb, var(--tenant-secondary, #111827) 68%, white);
    }
  `]
})
export class ShellSidebarComponent implements OnInit, OnDestroy
{
  private readonly authService = inject(AuthService);
  private readonly i18n = inject(I18nService);
  private readonly appointmentsService = inject(AppointmentsService);
  private readonly downtimeService = inject(DowntimeService);
  private readonly specialistDowntimeService = inject(SpecialistDowntimeService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly tenantBranding = inject(TenantBrandingService);
  private readonly router = inject(Router);
  private readonly subscriptions = new Subscription();

  protected readonly languageOptions = this.i18n.languages;
  protected appointmentsHasPending = false;
  protected downtimeHasPending = false;
  protected tenantDisplayName = '';
  protected tenantLogoUrl: string | null = null;

  private readonly allNavigationItems: Array<AppNavigationItem & { labelKey: string; roles: UserRole[] }> = [
    {
      labelKey: 'nav.appointments',
      route: 'appointments',
      roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
    },
    {
      labelKey: 'nav.myProfile',
      route: 'my-profile',
      roles: ['client']
    },
    {
      labelKey: 'nav.slots',
      route: 'slots',
      roles: ['system_admin', 'company_admin', 'manager', 'reception', 'specialist']
    },
    {
      labelKey: 'nav.devices',
      route: 'devices',
      roles: ['system_admin', 'company_admin', 'manager']
    },
    {
      labelKey: 'nav.downtime',
      route: 'downtime',
      roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception']
    },
    {
      labelKey: 'nav.notification',
      route: 'notifications',
      roles: ['system_admin', 'company_admin', 'manager']
    },
    {
      labelKey: 'nav.users',
      route: 'users',
      roles: ['system_admin', 'company_admin', 'manager']
    },
    {
      labelKey: 'nav.procedureTypes',
      route: '/procedure-types',
      roles: ['system_admin', 'company_admin', 'manager']
    },
    {
      labelKey: 'nav.companies',
      route: 'companies',
      roles: ['system_admin']
    },
    {
      labelKey: 'nav.settings',
      route: 'settings',
      roles: ['system_admin', 'company_admin', 'manager']
    },
    {
      labelKey: 'nav.clinics',
      route: 'clinics',
      roles: ['system_admin', 'company_admin', 'manager']
    },
    {
      labelKey: 'nav.dataRetention',
      route: 'data-retention',
      roles: ['system_admin']
    },
    {
      labelKey: 'nav.security',
      route: 'security',
      roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
    },
    {
      labelKey: 'nav.connectors',
      route: 'connectors',
      roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
    }
  ];

  public ngOnInit(): void
  {
    this.bindBranding();

    this.refreshAppointmentsPendingIndicator();
    this.refreshDowntimePendingIndicator();

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event) =>
        {
          const url = (event as NavigationEnd).urlAfterRedirects ?? '';
          if (url.includes('/appointments'))
          {
            this.refreshAppointmentsPendingIndicator();
          }
          if (url.includes('/downtime'))
          {
            this.refreshDowntimePendingIndicator();
          }
        })
    );

    if ((this.authService.getCurrentRole() as UserRole | null) === 'system_admin')
    {
      this.subscriptions.add(
        this.systemAdminContext.companyId$.subscribe(() => {
          this.refreshAppointmentsPendingIndicator();
          this.refreshDowntimePendingIndicator();
        })
      );
      this.subscriptions.add(
        this.systemAdminContext.scopeMode$.subscribe(() => {
          this.refreshAppointmentsPendingIndicator();
          this.refreshDowntimePendingIndicator();
        })
      );
    }
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  protected get navigationItems(): Array<AppNavigationItem & { labelKey: string }>
  {
    const role = this.authService.getCurrentRole() as UserRole | null;

    if (!role)
    {
      return this.allNavigationItems;
    }

    return this.allNavigationItems.filter((item) => item.roles.includes(role));
  }

  protected toRoute(route: string): string[]
  {
    const normalized = route.trim().replace(/^\/+/, '');
    const role = this.authService.getCurrentRole() as UserRole | null;
    const routeForRole = this.resolveRouteForRole(normalized, role);
    const segments = routeForRole.split('/').filter((segment) => segment.length > 0);
    const preferredSlug = this.authService.getPreferredCompanySlug();

    if (role !== 'system_admin' && preferredSlug)
    {
      return ['/', preferredSlug, ...segments];
    }

    return ['/', ...segments];
  }

  private resolveRouteForRole(route: string, role: UserRole | null): string
  {
    if (route === 'downtime')
    {
      return role === 'specialist' ? 'downtime/specialist' : 'downtime/device';
    }

    return route;
  }

  protected isAppointmentsRoute(route: string): boolean
  {
    return route.trim().replace(/^\/+/, '') === 'appointments';
  }

  protected isDowntimeRoute(route: string): boolean
  {
    return route.trim().replace(/^\/+/, '') === 'downtime';
  }

  protected get currentLanguage(): AppLanguage {
    return this.i18n.language;
  }

  protected get userEmail(): string | null
  {
    return this.authService.getCurrentUserEmail();
  }

  protected get userRole(): string | null
  {
    return this.authService.getCurrentRole();
  }

  protected onLanguageChange(language: AppLanguage): void {
    this.i18n.setLanguage(language);
  }

  protected onLogout(): void
  {
    this.authService.logout();
  }

  private refreshAppointmentsPendingIndicator(): void
  {
    if (!this.canLoadAppointmentsPendingSummary())
    {
      this.appointmentsHasPending = false;
      return;
    }

    this.appointmentsService.getPendingSummary().pipe(
      map((summary) => summary.hasAnyPending),
      catchError(() => of(false)),
    ).subscribe((hasPending) =>
    {
      this.appointmentsHasPending = hasPending;
    });
  }

  private refreshDowntimePendingIndicator(): void
  {
    if (!this.canLoadDowntimePendingSummary())
    {
      this.downtimeHasPending = false;
      return;
    }

    const role = this.authService.getCurrentRole() as UserRole | null;
    const canLoadDevice = role !== 'specialist';
    const canLoadSpecialist = role !== 'technician';

    const device$ = canLoadDevice
      ? this.downtimeService.getPendingSummary().pipe(map((summary) => summary.hasPending), catchError(() => of(false)))
      : of(false);
    const specialist$ = canLoadSpecialist
      ? this.specialistDowntimeService.getPendingSummary().pipe(map((summary) => summary.hasPending), catchError(() => of(false)))
      : of(false);

    forkJoin({
      deviceHasPending: device$,
      specialistHasPending: specialist$,
    }).subscribe(({ deviceHasPending, specialistHasPending }) => {
      this.downtimeHasPending = deviceHasPending || specialistHasPending;
    });
  }

  private canLoadDowntimePendingSummary(): boolean
  {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (!role || role === 'client')
    {
      return false;
    }

    if (role !== 'system_admin')
    {
      return true;
    }

    if (this.systemAdminContext.isGlobalScope())
    {
      return false;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  private canLoadAppointmentsPendingSummary(): boolean
  {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (!role)
    {
      return false;
    }

    if (role !== 'system_admin')
    {
      return true;
    }

    if (this.systemAdminContext.isGlobalScope())
    {
      return false;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  private bindBranding(): void {
    this.applyBrandingForCurrentContext();

    if ((this.authService.getCurrentRole() as UserRole | null) !== 'system_admin') {
      return;
    }

    this.subscriptions.add(
      this.systemAdminContext.companyId$.subscribe(() => {
        this.applyBrandingForCurrentContext();
      }),
    );
    this.subscriptions.add(
      this.systemAdminContext.scopeMode$.subscribe(() => {
        this.applyBrandingForCurrentContext();
      }),
    );
  }

  private applyBrandingForCurrentContext(): void {
    const role = this.authService.getCurrentRole() as UserRole | null;
    if (role === 'system_admin') {
      if (this.systemAdminContext.isGlobalScope()) {
        this.tenantDisplayName = '';
        this.tenantLogoUrl = null;
        return;
      }

      const companyId = this.systemAdminContext.getCompanyId();
      if (!companyId?.trim()) {
        this.tenantDisplayName = '';
        this.tenantLogoUrl = null;
        return;
      }

      this.subscriptions.add(
        this.tenantBranding.getBrandingByCompanyId(companyId).subscribe((branding) => {
          this.tenantDisplayName = branding?.displayName ?? '';
          this.tenantLogoUrl = branding?.logoUrl ?? null;
        }),
      );
      return;
    }

    const preferredSlug = this.authService.getPreferredCompanySlug();
    if (!preferredSlug) {
      this.tenantDisplayName = '';
      this.tenantLogoUrl = null;
      return;
    }

    this.subscriptions.add(
      this.tenantBranding.getBranding(preferredSlug).subscribe((branding) => {
        this.tenantDisplayName = branding?.displayName ?? '';
        this.tenantLogoUrl = branding?.logoUrl ?? null;
      }),
    );
  }
}
